import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, ShieldAlert, XCircle } from 'lucide-react'
import {
  Card, CardBody, CardHeader, ErrorState, LoadingState, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchSystemHealth, formatCount, formatDate, formatDateTime, translateError,
  type HealthCheck, type HealthState,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-131 + W-133 — WHAT THIS SERVER CAN DO, AND WHAT THE LOG IS ABOUT TO FORGET.
   Sprint 70.

   ── THE FACTS EXISTED; NOBODY WITHOUT A TERMINAL COULD SEE THEM ────────────────
   `/health` names the identity provider. The PDF renderer says at start-up whether there
   is a browser on the machine. The file store knows whether it is local disk. Every one of
   those went to a log nobody in the hotel reads, and the consequence was the same each
   time: **a thing that quietly does not work looks exactly like a thing that works until
   somebody needs it** — the supervisor at the moment they try to print a month's report,
   the administrator in the week the server is replaced.

   ── THREE STATES, AND THE MIDDLE ONE IS THE WHOLE POINT ────────────────────────
   Working, not working, and *working but not in a way anybody should go live on*. A
   preview identity provider and an unbacked-up disk are both the third thing, and a
   two-state screen would have to draw them green.

   ── IT REPORTS AND IT DOES NOT FIX ─────────────────────────────────────────────
   Nothing here is a button. Every line is a fact about how the server was started, and
   changing one is a deployment decision taken by somebody with access to the machine.

   ── AND RETENTION IS SHOWN, NOT EDITED ─────────────────────────────────────────
   Deliberately. A box that lowered it from twenty-four months to three would be a way to
   destroy twenty-one months of evidence with a number, silently, and the people who would
   have wanted those rows are exactly the people who would not be told.
   ========================================================================= */

const TONE: Record<HealthState, { colour: string; Icon: typeof CheckCircle2 }> = {
  OK: { colour: 'var(--hk-hk-ready-text)', Icon: CheckCircle2 },
  PREVIEW: { colour: 'var(--hk-hk-prog-text)', Icon: ShieldAlert },
  MISSING: { colour: 'var(--hk-hk-dirty-text)', Icon: XCircle },
}

export function System({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const health = useQuery({
    queryKey: ['system-health', propertyId],
    queryFn: ({ signal }) => fetchSystemHealth(propertyId, { signal }),
    retry: false,
  })

  if (health.isLoading && !health.error) return <LoadingState rows={4} />

  if (health.error && !health.data) {
    if (health.error instanceof ApiError && health.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(health.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={health.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = health.data!
  const notReady = view.checks.filter((c) => c.state !== 'OK')

  return (
    <div className="space-y-4 p-4">
      {/* **THE SENTENCE FIRST.** A list of ticks is read as "fine"; a sentence naming the
          number that is not is read. */}
      <p className="text-sm">
        {notReady.length === 0
          ? t.sysAllWell
          : fill(t.sysNotReady, { count: formatCount(notReady.length, locale) })}
      </p>

      <Card>
        <CardHeader title={t.sysChecksTitle} icon={<CheckCircle2 className="size-4" />} />
        <CardBody className="space-y-2">
          {view.checks.map((check) => <Check key={check.code} check={check} />)}
        </CardBody>
      </Card>

      {view.blockedOn.length > 0 && (
        <Card>
          <CardHeader title={t.sysBlockedTitle} icon={<AlertTriangle className="size-4" />} />
          <CardBody className="space-y-1">
            {/* **NOT DEFECTS AND NOT OURS TO CLOSE.** A health screen showing only the
                parts we control would read as "everything is fine" on a server that cannot
                verify a token. */}
            <p className="text-sm text-muted-foreground">{t.sysBlockedBody}</p>
            {view.blockedOn.map((item) => (
              <p key={item} className="text-sm"><span dir="ltr">{item}</span></p>
            ))}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title={t.sysRetentionTitle} />
        <CardBody className="space-y-1.5 text-sm">
          <p>{fill(t.sysRetentionBody, {
            months: formatCount(view.retention.months, locale),
          })}</p>

          <Line
            label={t.sysOldest}
            /* **NULL IS "NOTHING HAS BEEN WRITTEN", NOT A DATE.** An administrator looking
               for last March needs to tell "it was deleted" from "it never happened". */
            /* **WITH THE YEAR**, which `formatDateTime` does not carry. This row is at
               most twenty-four months old by definition, so "14 Oct" alone is the one
               format that cannot answer the only question being asked of it. Found by
               rendering it: the demo's oldest entry read as 14 October, and it is from
               2024. The hour is dropped in exchange — nobody asks what time of day the
               oldest surviving entry was written. */
            value={view.retention.oldestEntryUtc === null
              ? t.sysLogEmpty
              : formatDate(new Date(view.retention.oldestEntryUtc), locale)}
          />
          <Line label={t.sysHeld} value={formatCount(view.retention.entriesHeld, locale)} />
          <Line
            label={t.sysExpiring}
            value={formatCount(view.retention.expiringWithinMonth, locale)}
          />

          <p className="text-[12px] text-muted-foreground">{t.sysRetentionNote}</p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t.sysAboutTitle} />
        <CardBody className="space-y-1.5 text-sm">
          <Line label={t.sysEnvironment} value={view.environment} ltr />
          <Line label={t.sysVersion} value={view.version} ltr />
          {/* **BOTH CLOCKS.** They are three hours apart for a quarter of every day, and
              every screen in this product asks for the second one — an administrator
              wondering why today's board is empty should see both without a terminal. */}
          <Line
            label={t.sysServerTime}
            value={formatDateTime(new Date(view.serverTimeUtc), locale)}
          />
          <Line label={t.sysHotelZone} value={view.propertyTimeZone} ltr />
          {/* A DATE, drawn as one. It was `formatDateTime` and printed "19 Sept, 00:00",
              which invites the reader to compare a midnight against the server clock above
              it — when the thing worth comparing is the DATE, and three hours a night is
              when the two differ. */}
          <Line
            label={t.sysHotelDate}
            value={formatDate(new Date(view.propertyLocalDate), locale)}
          />
        </CardBody>
      </Card>
    </div>
  )
}

function Check({ check }: { check: HealthCheck }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const tone = TONE[check.state]

  return (
    <div className="flex items-start gap-2">
      <tone.Icon className="mt-0.5 size-4 shrink-0" style={{ color: tone.colour }} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">
          {t.sysChecks[check.code] ?? check.code}
        </span>
        {/* **THE NOTE IS TRANSLATED AND THE VALUE IS NOT**, which is the difference
            between a sentence and a name. `previewProvider` has an Arabic sentence;
            `preview-NO-REAL-AUTHENTICATION` is the thing somebody has to go and change on
            the server, and renaming it in Arabic would invent a name nobody could search
            for. The fallback to the bare code is deliberate — a gap should be legible
            rather than blank — and `systemContract.test.ts` is what stops the fallback
            from being the thing anybody ever sees. */}
        {check.note && (
          <span className="block text-[12px] text-muted-foreground">
            {t.sysNotes[check.note] ?? check.note}
          </span>
        )}
        {/* **ITS OWN LINE, AND A `bdi`.** The first draft joined the two with an em dash,
            which looked right in English and came out as `...حقيقيّين. — preview-NO-REAL`
            in Arabic: the dash resolved to the wrong side of the Latin token and the token
            wrapped through the middle of the sentence. A Latin name inside Arabic prose is
            what `bdi` is for, and a name on its own line needs no separator at all. */}
        {check.value && (
          /* **THE LINE FOLLOWS THE PAGE AND THE TOKEN DOES NOT.** `dir="ltr"` on the block
             itself pushed the name to the left edge of an Arabic card, detached from the
             sentence it belongs to. The block inherits the page's direction so it starts
             where the reader's eye is; the `bdi` keeps the token's own characters in
             order. */
          <span className="block text-[12px] text-muted-foreground">
            <bdi dir="ltr" className="tnum">{check.value}</bdi>
          </span>
        )}
      </span>
      <span className="shrink-0 text-[11.5px] font-medium" style={{ color: tone.colour }}>
        {t.sysStates[check.state]}
      </span>
    </div>
  )
}

function Line({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tnum font-medium ${ltr ? '' : ''}`} {...(ltr ? { dir: 'ltr' } : {})}>
        {value}
      </span>
    </div>
  )
}
